import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import ModalOpacity from 'ModalOpacity.js';
import AccountPayments from 'AccountPayments.js';
import UseToggle from 'UseToggle.js';
import { toggleProlongSessionModal } from "authActions.js";
import { translation } from 'utilsHelper';
import { getLanguageCookie } from 'cookiesHelper';
import PaymentIcon from 'exchange_alt.svg';
import S from 'StyledInstantWinGamesTopTab.js';

const WITHDRAWAL_CONFIRMATION_OPENED = "withdrawalConfirmationOpened";

const InstantWinGamesTopTab = ({ brandData, user }) => {
  const { login, balance = 0 } = user ?? {};
  const isCustomerTypeIWG = login?.startsWith("session_user_");

  const paymentData = {
    currencyCode: brandData?.mainCurrency,
    countryCode: brandData?.countryCode,
    lang: getLanguageCookie(),
  };

  const dispatch = useDispatch()

  const [showPaymentModal, togglePaymentModal] = UseToggle(undefined);
  const [withdrawalConfirmationOpen, setWithdrawalConfirmationOpen] = useState(false);
  const prevShowPaymentModal = useRef(showPaymentModal);

  const handlePaymentWidgetClose = useCallback(() => {
    setWithdrawalConfirmationOpen(false);
    togglePaymentModal(false);
  }, [togglePaymentModal]);

  const handleWithdrawalConfirmationOpened = useCallback(() => {
    setWithdrawalConfirmationOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("paymentWidgetModalClose", handlePaymentWidgetClose);
    window.addEventListener(WITHDRAWAL_CONFIRMATION_OPENED, handleWithdrawalConfirmationOpened);
    return () => {
      window.removeEventListener("paymentWidgetModalClose", handlePaymentWidgetClose);
      window.removeEventListener(WITHDRAWAL_CONFIRMATION_OPENED, handleWithdrawalConfirmationOpened);
    };
  }, [handlePaymentWidgetClose, handleWithdrawalConfirmationOpened]);

  // Reset flag only when payment modal transitions from open to closed (e.g. user closes by X), so session modal can show
  useEffect(() => {
    if (prevShowPaymentModal.current === true && showPaymentModal === false) {
      setWithdrawalConfirmationOpen(false);
    }
    prevShowPaymentModal.current = showPaymentModal;
  }, [showPaymentModal]);

  // Automatically log out IWG user, if balance is equal 0 (only after payment modal and withdrawal confirmation are closed)
  useEffect(() => {
    if (
      isCustomerTypeIWG &&
      balance === 0 &&
      showPaymentModal === false &&
      withdrawalConfirmationOpen === false
    ) {
      dispatch(toggleProlongSessionModal({ isOpen: true, sessionClosed: true }));
    }
  }, [isCustomerTypeIWG, balance, showPaymentModal, withdrawalConfirmationOpen])

  return brandData ? (
    <S.InstantWinGamesTopTab>
      <ModalOpacity
        isOpen={showPaymentModal}
        toggleOpen={() => togglePaymentModal()}
        flexDirectionBody="column"
        title={translation('instantWinGames_deposit')}
        titleIcon={PaymentIcon}
        titleIconWidth="20px"
        titleIconHeight={null}
        titleFontSize="14px"
        titleColor="white"
        closeIconColor="white"
        headerPadding="1.25rem"
        backgroundHeader="#009A3F"
        padding="0px"
      >
        <S.PaymentBalanceInfo>
          <S.PaymentSessionWrapper isSessionActive={!!user}>
            <span>
              {!!user ? translation('session_active') : translation('session_deactive')} -{' '}
              {login ? translation('session_user', { id: login }) : translation('deposit_to_play')}
            </span>
          </S.PaymentSessionWrapper>
          <S.PaymentBalanceWrapper>
            <span>{translation('balance')}:</span>
            <S.PaymentBalanceBox>{balance.toFixed(2)}</S.PaymentBalanceBox>
            <S.InfoIcon>i</S.InfoIcon>
          </S.PaymentBalanceWrapper>
        </S.PaymentBalanceInfo>
        <AccountPayments {...paymentData} />
      </ModalOpacity>

      <S.BalanceWrapper className="balance">
        <S.BalanceLabel>{translation('balance')}</S.BalanceLabel>
        <S.BalanceBox>{balance.toFixed(2)}</S.BalanceBox>
        <S.DepositButton onClick={() => togglePaymentModal()}>
          <S.DepositIcon dangerouslySetInnerHTML={{ __html: PaymentIcon }} />
          {translation('instantWinGames_deposit')}
        </S.DepositButton>
      </S.BalanceWrapper>
    </S.InstantWinGamesTopTab>
  ) : null;
};

const mapStateToProps = ({ Auth: { brandData, user } }) => {
  return {
    brandData,
    user,
  };
};

export default connect(mapStateToProps, null)(InstantWinGamesTopTab);
